import React, { useEffect, useState } from 'react';
import { LLMIcon } from '../../../../assets/images';
import BasicModal from '../../../../components/action-modal';
import { AddEditEmbeddedLLMProps, EmbeddedLlmDetailsProps } from '../embedded.props';
import { validateNumberField, validateTextField } from '../../../../services';
import { createEmbeddedModel, editEmbeddedModel } from '../services/embedded-llm';
import { FilterDropdown, MakeToast } from '../../../../components';
import {
  AddProviderText,
  DescriptionText,
  ModelNameText,
  PromptTokenText,
} from '../data/constants';

function AddEditEmBeddedLLM({
  providers,
  dataToEdit,
  open,
  setOpen,
  setDataUpdated,
}: AddEditEmbeddedLLMProps) {
  const [llmDetails, setLLMDetails] = useState<EmbeddedLlmDetailsProps>({
    provider_id: 0,
    name: '',
    description: '',
    input_token_pricing: undefined,
  });
  const [errorsMsgs, setErrorsMsgs] = useState({
    name: '',
    input_token_pricing: '',
  });

  useEffect(() => {
    if (open && dataToEdit.id !== 0) {
      setLLMDetails({
        id: dataToEdit?.id,
        provider_id: dataToEdit.provider_id,
        name: dataToEdit.name,
        description: dataToEdit.description,
        input_token_pricing: dataToEdit.input_token_pricing,
      });
    } else {
      setLLMDetails((prev) => ({
        ...prev,
        provider_id: providers?.[0]?.id || 0,
      }));
    }
  }, [open, dataToEdit]);

  const handleDataChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    const updatedValue =
      name === 'provider_id' || name === 'input_token_pricing' ? Number(value) : value;
    let error = '';
    if (name !== 'description') {
      if (name === 'input_token_pricing') {
        error = validateNumberField(value, 'Prompt token cost');
      } else {
        error = validateTextField(value, 'Model Name');
      }
    }
    setErrorsMsgs((prevErrors) => ({ ...prevErrors, [name]: error }));
    setLLMDetails((prevDetails) => ({
      ...prevDetails,
      [name]: updatedValue,
    }));
  };

  const handleCancelClick = () => {
    setLLMDetails({
      name: '',
      description: '',
      provider_id: 1,
      input_token_pricing: undefined,
    });
    setOpen(false);
    setErrorsMsgs({
      name: '',
      input_token_pricing: '',
    });
  };

  const saveEmbeddedLLM = () => {
    let apiCall;
    const { name, input_token_pricing: inputTokenPricing } = llmDetails;
    const nameError = validateTextField(name, 'Model Name');
    const promptTokenError = validateNumberField(String(inputTokenPricing), 'Prompt Token Cost');
    setErrorsMsgs({
      name: nameError,
      input_token_pricing: promptTokenError,
    });
    if (nameError || promptTokenError) {
      return;
    }
    if (dataToEdit.id && dataToEdit.id !== 0) {
      apiCall = editEmbeddedModel(llmDetails);
    } else {
      apiCall = createEmbeddedModel(llmDetails);
    }
    apiCall
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          setDataUpdated((prev) => !prev);
          handleCancelClick();
          MakeToast(
            'success',
            dataToEdit?.id && dataToEdit.id !== 0
              ? 'Embedding has been updated!'
              : 'Embedding has been created!',
          );
        } else if (res.status === 401) {
          // Handle logout if needed
        }
      })
      .catch((err) => {
        MakeToast(
          'error',
          err?.response?.data?.message
            ? `${err?.response?.data?.message}.`
            : 'Something went wrong!',
        );
      });
  };

  return (
    <BasicModal
      className="w-[550px]"
      open={open}
      modalTitle="Add Embedding"
      modalDesc="Add embedding config to the system"
      modalLogo={LLMIcon}
      resetData={handleCancelClick}
      saveData={saveEmbeddedLLM}
      confirmButtonText="Save"
    >
      <div className="flex flex-col text-sm w-full">
        <span className="text-[#344054] font-medium">
          {AddProviderText}
          <span className="text-red-600">*</span>
        </span>
        <FilterDropdown
          options={providers?.map((item) => ({ value: String(item.id), label: item.name })) ?? []}
          value={String(llmDetails?.provider_id ?? '')}
          onChange={(v: string) =>
            handleDataChange({
              target: { name: 'provider_id', value: v },
            } as React.ChangeEvent<HTMLSelectElement>)
          }
          placeholder="Select provider"
          showClear={false}
          className="mt-1 border border-[#E4E4E7] rounded-lg w-full px-3 py-2 text-left flex items-center justify-between"
        />
      </div>
      <div className="flex flex-col bg-[#E0E6F6] p-[15px] rounded-lg gap-3">
        <span className="font-bold text-base">
          {providers?.find((item) => item.id === llmDetails?.provider_id)?.name} (Provider)
        </span>
        <div className="flex flex-col text-sm">
          <span className="text-[#344054] font-medium">
            {ModelNameText}
            <span className="text-red-600">*</span>
          </span>
          <input
            placeholder="Enter model name"
            className={`mt-1 text-sm py-[10px] ${
              errorsMsgs.name ? 'focus:border-red-600 border-red-600' : 'border-[#E4E4E7]'
            } rounded-lg w-full`}
            value={llmDetails?.name}
            name="name"
            onChange={handleDataChange}
          />
          {errorsMsgs.name && <span className="text-red-600">{errorsMsgs.name}</span>}
        </div>
        <div className="flex flex-col text-sm w-full">
          <span className="text-[#344054] font-medium">
            {PromptTokenText}
            <span className="text-red-600">*</span>
          </span>
          <input
            type="number"
            placeholder="Enter prompt token cost"
            className={`mt-1 text-sm py-[10px] ${
              errorsMsgs.input_token_pricing
                ? 'focus:border-red-600 border-red-600'
                : 'border-[#E4E4E7]'
            } rounded-lg w-full`}
            value={llmDetails?.input_token_pricing ?? ''}
            name="input_token_pricing"
            onChange={handleDataChange}
            min={0}
          />
          {errorsMsgs.input_token_pricing && (
            <span className="text-red-600">{errorsMsgs.input_token_pricing}</span>
          )}
        </div>
        <div className="flex flex-col text-sm">
          <span className="text-[#344054] font-medium">{DescriptionText}</span>
          <textarea
            placeholder="Enter description"
            className="mt-1 text-sm border border-[#E4E4E7] rounded-lg"
            value={llmDetails?.description}
            name="description"
            onChange={handleDataChange}
          />
        </div>
      </div>
    </BasicModal>
  );
}

export default AddEditEmBeddedLLM;
